import React, { useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Switch,
  Paper,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  LocalOffer as DiscountIcon,
  Receipt as ReceiptIcon,
  LocalShipping as ShippingIcon,
} from "@mui/icons-material";

export default function TotalsFooter({
  subtotal,
  discount, // giá trị người nhập
  discountType, // "amount" | "percent"
  discountValue, // số tiền đã trừ (tính ở parent)
  finalTotal, // tổng sau giảm + ship (tính ở parent)
  setDiscount,
  setDiscountType,
  shippingEnabled,
  setShippingEnabled,
  onCheckout,
  canCheckout,
  shippingFee = 35000, // ✅ optional
}) {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const formatMoney = useCallback((v) => Number(v || 0).toLocaleString("vi-VN"), []);

  // Clamp theo loại
  const clampDiscount = useCallback(
    (num, type) => {
      if (type === "percent") return Math.max(0, Math.min(100, num));
      return Math.max(0, Math.min(subtotal || 0, num));
    },
    [subtotal]
  );

  // --- xử lý input ---
  const handleDiscountChange = useCallback(
    (e) => {
      const raw = e.target.value;
      const num = Number(String(raw).replace(/[^\d.]/g, ""));
      if (Number.isNaN(num)) return;
      setDiscount(clampDiscount(num, discountType));
    },
    [discountType, clampDiscount, setDiscount]
  );

  // --- đổi loại giảm giá ---
  const handleTypeChange = useCallback(
    (_e, next) => {
      if (!next) return;

      // chuyển đổi giá trị đang có sang type mới, cố gắng giữ "ý nghĩa" nhất có thể
      if (next === "percent") {
        const percent =
          subtotal > 0 ? (Number(discount || 0) / Math.max(1, subtotal)) * 100 : 0;
        setDiscount(Number(clampDiscount(Number(percent.toFixed(2)), "percent")));
      } else {
        const amount = Math.round((Number(discount || 0) / 100) * (subtotal || 0));
        setDiscount(clampDiscount(amount, "amount"));
      }

      setDiscountType(next);
    },
    [subtotal, discount, clampDiscount, setDiscount, setDiscountType]
  );

  const disabledDiscount = useMemo(() => subtotal <= 0, [subtotal]);

  return (
    <Paper
      elevation={smDown ? 8 : 0}
      sx={{
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: "background.paper",
        width: "100%",
        position: { xs: "fixed", md: "sticky" },
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        px: { xs: 1.5, md: 2 },
        py: { xs: 1.25, md: 2 },
        pb: { xs: `calc(12px + env(safe-area-inset-bottom))`, md: 2 },
      }}
    >
      {/* Row: Subtotal */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography color="text.secondary">Tổng tiền hàng</Typography>
        <Typography fontWeight={900}>{formatMoney(subtotal)}₫</Typography>
      </Box>

      {/* Discount */}
      <Box sx={{ mt: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <DiscountIcon fontSize="small" />
            <Typography fontWeight={800}>Giảm giá</Typography>
          </Box>

          <Chip
            size="small"
            label={`- ${formatMoney(discountValue)}₫${discountType === "percent" && subtotal > 0 ? ` (${discount}%)` : ""}`}
            variant="outlined"
            sx={{ fontWeight: 800 }}
          />
        </Box>

        <Box
          sx={{
            mt: 1,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          <TextField
            value={discount}
            onChange={handleDiscountChange}
            type="number"
            size="small"
            disabled={disabledDiscount}
            inputProps={{
              min: 0,
              step: discountType === "percent" ? "0.1" : "1000",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DiscountIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 180 },
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
            placeholder={discountType === "percent" ? "Nhập %" : "Nhập số tiền"}
          />

          <ToggleButtonGroup
            value={discountType}
            exclusive
            onChange={handleTypeChange}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                borderRadius: 2,
                px: 1.25,
                fontWeight: 900,
              },
            }}
          >
            <ToggleButton value="amount">₫</ToggleButton>
            <ToggleButton value="percent">%</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ flex: 1 }} />
        </Box>
      </Box>

      {/* Shipping */}
      <Box
        sx={{
          mt: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <ShippingIcon fontSize="small" />
          <Typography fontWeight={800}>Giao hàng</Typography>
        </Box>

        <FormControlLabel
          sx={{ m: 0 }}
          control={
            <Switch
              checked={shippingEnabled}
              onChange={(e) => setShippingEnabled(e.target.checked)}
              color="primary"
            />
          }
          label={
            shippingEnabled ? `+${formatMoney(shippingFee)}₫` : "Miễn phí"
          }
        />
      </Box>

      <Divider sx={{ my: 1.25 }} />

      {/* Total */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Typography variant="subtitle1" fontWeight={900}>
          Tổng cộng
        </Typography>

        <Typography
          variant={smDown ? "h6" : "h5"}
          color="primary"
          fontWeight={900}
          sx={{ lineHeight: 1.1 }}
        >
          {formatMoney(finalTotal)}₫
        </Typography>
      </Box>

      {/* Checkout button */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        startIcon={<ReceiptIcon />}
        onClick={onCheckout}
        disabled={!canCheckout}
        sx={{
          mt: 1.25,
          py: 1.25,
          borderRadius: 2,
          fontWeight: 900,
          letterSpacing: 0.2,
        }}
      >
        {canCheckout ? "THANH TOÁN" : "CHỌN SẢN PHẨM ĐỂ THANH TOÁN"}
      </Button>
    </Paper>
  );
}
